import React, { useMemo, useState } from 'react';
import { defineMessages } from 'react-intl';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { HistoryOutlined } from '@ant-design/icons';
import OSMMap from '@components/common/form/OSMMap';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DEFAULT_FORMAT, DEFAULT_TABLE_ITEM_SIZE, DELIVERY_TRIP_STATE_PENDING } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { deliveryTripStateOptions } from '@constants/masterData';
import useFetch from '@hooks/useFetch';
import useListModal from '@hooks/useListModal';
import useQueryParams from '@hooks/useQueryParams';
import useRestaurant from '@hooks/useRestaurant';
import useTranslate from '@hooks/useTranslate';
import { BaseTable } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import { IconMap2 } from '@tabler/icons-react';
import { convertUtcToLocalTime } from '@utils';
import { Button, Modal, Tag, Tooltip } from 'antd';

const message = defineMessages({
    objectName: 'Delivery Trip',
});

const DeliveryTripListPage = () => {
    const translate = useTranslate();
    const navigate = useNavigate();

    const { params } = useQueryParams();
    const driverName = params.get('driverName');
    const { restaurantId, driverId } = useParams();
    const stateValue = translate.formatKeys(deliveryTripStateOptions, ['label']);

    const [isMapModalOpen, setIsMapModalOpen] = useState(false);
    const [selectedTrip, setSelectedTrip] = useState(null);

    const { restaurant } = useRestaurant();
    const currentRestaurant = useMemo(() => {
        if (!restaurant || !Array.isArray(restaurant)) return null;
        return restaurant.find(item => String(item.id) === String(restaurantId));
    }, [restaurant, restaurantId]);

    const { execute: executeGet, loading: loadingGet } = useFetch(apiConfig.deliveryTrip.getById, { immediate: false });
    const handleGetDeliveryTripById = (id) => {
        executeGet({
            pathParams: { id },
            onCompleted: (response) => {
                if (response.result === true) {
                    const data = response.data;
                
                    const waypointsData = JSON.parse(data.waypoints);

                    const nextDeliveryId = data.deliveryTripDetails.find(d => d.state === 0)?.id;
                    const isAllDone = data.deliveryTripDetails.every(detail => detail.state !== 0);

                    const restaurantMarker = currentRestaurant ? {
                        id: 'restaurant-start',
                        position: [currentRestaurant.latitude, currentRestaurant.longitude],
                        color: '#038a19', 
                        iconStyle: {         
                            outline: '3px solid white',
                            outlineOffset: '-4px',
                        },
                    } : null;
            
                    const orderMarkers = data.deliveryTripDetails.map((detail) => ({
                        id: detail.id,
                        position: [detail.order.customer.latitude, detail.order.customer.longitude],
                        color: detail.state === 0 ? '#ff8c00' : '#038a19',
                        iconStyle: detail.id === nextDeliveryId ? {         
                            outline: '3px solid white',
                            outlineOffset: '-4px',
                        } : {},
                    }));

                    const encodedRoutes = waypointsData.legs.map((leg) => {
                        const detail = data.deliveryTripDetails.find(d => String(d.id) === String(leg.destId));
                    
                        let routeColor = '#ff8c00';
                        if (detail) {
                            routeColor = detail.state === 0 ? '#ff8c00' : '#038a19';
                        } else {
                            routeColor = isAllDone ? '#038a19' : '#ff8c00';
                        }

                        return {
                            encodedPath: leg.geometry,
                            color: routeColor,
                        };
                    });

                    const markers = restaurantMarker ? [restaurantMarker, ...orderMarkers] : orderMarkers;

                    setSelectedTrip({ markers, encodedRoutes });
                    setIsMapModalOpen(true);
                }
            },
        });
    };

    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
    } = useListModal({
        apiConfig: apiConfig.deliveryTrip,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            const originalPrepareGetListParams = funcs.prepareGetListParams;
            funcs.prepareGetListParams = (filter) => {
                const params = originalPrepareGetListParams(filter);
                return {
                    ...params,
                    driverId,
                };
            };
            const originalChangeFilter = funcs.changeFilter;
            funcs.changeFilter = (filter) => {
                originalChangeFilter({
                    driverName,
                    ...filter,
                });
            };
            funcs.additionalActionColumnButtons = () => ({
                viewMap: (dataRow) => {
                    const hasPerm = funcs.hasPermission([apiConfig.deliveryTrip.getById.baseURL]) && dataRow?.state !== DELIVERY_TRIP_STATE_PENDING;
                    return (
                        <Tooltip title={translate.formatMessage(commonMessage.viewMap)}>
                            <Button
                                type="link"
                                style={{ padding: 0 }}
                                disabled={!hasPerm}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleGetDeliveryTripById(dataRow.id);
                                }}
                            >
                                <IconMap2 size={16} style={{ color: hasPerm ? '#038a19' : 'gray' }} />
                            </Button>
                        </Tooltip>
                    );
                },
                deliveryTripDetail: (dataRow) => {
                    return (
                        <Tooltip title={translate.formatMessage(commonMessage.deliveryTripDetail)}>
                            <Button
                                type="link"
                                style={{ padding: 0 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(
                                        `${generatePath(routes.driverDeliveryTripDetailListPage.path, {
                                            restaurantId,
                                            driverId,
                                            deliveryTripId: dataRow.id,
                                        // })}?driverName=${encodeURIComponent(driverName || '')}&deliveryTripName=${encodeURIComponent(dataRow?.name || '')}`,
                                        })}?driverName=${encodeURIComponent(driverName || '')}`,
                                    );
                                }}
                            >
                                <HistoryOutlined style={{ color: '#428ef6' }} />
                            </Button>
                        </Tooltip>
                    );
                },
            });
        },
    });

    const columns = [
        {
            title: translate.formatMessage(commonMessage.startTime),
            dataIndex: 'startTime',
            render: (startTime) => {
                const createdDateLocal = convertUtcToLocalTime(startTime, DEFAULT_FORMAT, DEFAULT_FORMAT);
                return <div>{createdDateLocal}</div>;
            },
        },
        {
            title: translate.formatMessage(commonMessage.endTimeEstimate),
            dataIndex: 'endTimeEstimate',
            render: (endTimeEstimate) => {
                const createdDateLocal = convertUtcToLocalTime(endTimeEstimate, DEFAULT_FORMAT, DEFAULT_FORMAT);
                return <div>{createdDateLocal}</div>;
            },
        },
        {
            title: translate.formatMessage(commonMessage.endTimeEstimate),
            dataIndex: 'endTimeActual',
            render: (endTimeActual) => {
                const createdDateLocal = convertUtcToLocalTime(endTimeActual, DEFAULT_FORMAT, DEFAULT_FORMAT);
                return <div>{createdDateLocal}</div>;
            },
        },
        {
            title: translate.formatMessage(commonMessage.totalDistance),
            dataIndex: 'totalDistance',
            align: 'center',
        },
        {
            title: translate.formatMessage(commonMessage.state),
            dataIndex: 'state',
            align: 'center',
            render: (state) => {
                const currentState = stateValue?.find(item => item.value === state);
                    
                return (
                    <Tag color={currentState?.color}>
                        <div style={{ padding: '0 4px', fontSize: 14 }}>{currentState?.label}</div>
                    </Tag>
                );
            },
        },
        mixinFuncs.renderActionColumn(
            {
                viewMap: true,
                deliveryTripDetail: true,
            },
            { width: '120px' },
        ),
    ];

    const searchFields = [
        {
            key: 'state',
            placeholder: translate.formatMessage(commonMessage.state),
            type: FieldTypes.SELECT,
            options: stateValue,
            submitOnChanged: true,
            colSpan: 4,
        },
    ];


    return (
        <PageWrapper
            routes={[
                {
                    breadcrumbName: translate.formatMessage(commonMessage.drivers),
                    path: `/${restaurantId}/driver`,
                },
                {  breadcrumbName: driverName || translate.formatMessage(message.objectName) },
            ]}
            loading={loadingGet}
        >
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        style={{ cursor: 'pointer' }}
                        loading={loading}
                    />
                }
            />
            {(selectedTrip && isMapModalOpen) && (
                <Modal
                    title={translate.formatMessage(commonMessage.viewMap)}
                    open={isMapModalOpen}
                    onCancel={() => {
                        setIsMapModalOpen(false);
                        setSelectedTrip(null);
                    }}
                    footer={null} 
                    width="80%"
                    centered
                    destroyOnClose
                    afterOpenChange={(open) => {
                        if (open) window.dispatchEvent(new Event('resize'));
                    }}
                >
                    <div style={{ height: '80vh', width: '100%' }}>
                        <OSMMap
                            // center={selectedTrip.markers[0]?.position}
                            markers={selectedTrip.markers}
                            encodedRoutes={selectedTrip.encodedRoutes}
                            // zoom={14}
                            style={{ height: '100%' }}
                        />
                    </div>
                </Modal>
            )}
            
        </PageWrapper>
    );
};

export default DeliveryTripListPage;